import { useCallback, useEffect, useMemo, useRef } from 'react'

import QRCodeStyling from 'qr-code-styling'
import { useTranslation } from 'react-i18next'

import { useModal } from '@app/hooks'
import { trackDownloadAppModalOpened } from '@app/services/tracking'
import { isAndroid, isIos } from '@app/utils'

import { Button, Icon, Modal } from '@lib/components'

import {
  APP_STORE_APP_URL,
  DOWNLOAD_APP_LINK_PATH,
  DOWNLOAD_APP_MODAL_KEY,
  PLAY_STORE_APP_URL,
  QR_CODE_STYLING_OPTIONS,
} from './constants'
import styles from './styles.module.scss'
import { DownloadPromptProps } from './types'

export const DownloadPrompt = ({ showPrompt = true, showModal = true }: DownloadPromptProps) => {
  const { t } = useTranslation()
  const { open, close, isOpen } = useModal(DOWNLOAD_APP_MODAL_KEY)
  const qrContainerRef = useRef<HTMLDivElement | null>(null)
  const qrCodeRef = useRef<QRCodeStyling | null>(null)
  const { isIosDevice, isAndroidDevice } = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return { isIosDevice: false, isAndroidDevice: false }
    }

    return {
      isIosDevice: isIos(),
      isAndroidDevice: isAndroid(),
    }
  }, [])
  const directStoreUrl = useMemo(() => {
    if (isIosDevice) {
      return APP_STORE_APP_URL
    }

    if (isAndroidDevice) {
      return PLAY_STORE_APP_URL
    }

    return null
  }, [isIosDevice, isAndroidDevice])
  const qrCodeUrl = `${document.location.origin}${DOWNLOAD_APP_LINK_PATH}`

  useEffect(() => {
    if (!qrCodeUrl || directStoreUrl) {
      return
    }

    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling({
        ...QR_CODE_STYLING_OPTIONS,
        data: qrCodeUrl,
      })
    } else {
      qrCodeRef.current.update({ data: qrCodeUrl })
    }
  }, [directStoreUrl, qrCodeUrl])

  useEffect(() => {
    if (directStoreUrl) {
      return
    }

    const container = qrContainerRef.current
    if (!container || !qrCodeRef.current) {
      return
    }

    container.innerHTML = ''
    qrCodeRef.current.append(container)

    return () => {
      container.innerHTML = ''
    }
  }, [directStoreUrl, isOpen, qrCodeUrl])

  const handleOpenModal = useCallback(() => {
    trackDownloadAppModalOpened()
    open()
  }, [open])

  const handleCloseModal = useCallback(() => {
    close()
  }, [close])

  const handleOpenStore = useCallback(() => {
    if (!directStoreUrl) {
      return
    }

    window.location.assign(directStoreUrl)
  }, [directStoreUrl])

  return (
    <>
      {showPrompt ? (
        <>
          <Icon path="download-app" />
          <h2>{t('MobileAppAd.Chat.Title')}</h2>
          <div className={styles.downloadAppMessage}>{t('MobileAppAd.Alerts.Description2')}</div>
          <Button variant={'primary'} onClick={directStoreUrl ? handleOpenStore : handleOpenModal}>
            <div className={styles.buttonWrapper}>
              <Icon path="icons/icon-qr-code" />
              {t('MobileAppAd.Button.GetTheApp')}
            </div>
          </Button>
        </>
      ) : null}

      {showModal && !directStoreUrl && (
        <Modal
          title=""
          isOpen={isOpen}
          isFullScreen={false}
          showBottomCloseButton={false}
          className={styles.downloadAppModal}
          onClose={handleCloseModal}
        >
          <div className={styles.downloadAppModalContent}>
            <h1>{t('MobileAppAd.Qr.Title')}</h1>
            <div className={styles.text}>{t('MobileAppAd.Qr.Description1')}</div>
            {qrCodeUrl && <div ref={qrContainerRef} className={styles.qrCodeWrapper} />}
            <div className={styles.separator}>
              <div className={styles.line} />
              <div className={styles.text}>{t('MobileAppAd.Qr.Description2')}</div>
              <div className={styles.line} />
            </div>
            <div className={styles.buttons}>
              <a href={APP_STORE_APP_URL} target="_blank" rel="noreferrer">
                <Icon path="download-app-apple" />
              </a>
              <a href={PLAY_STORE_APP_URL} target="_blank" rel="noreferrer">
                <Icon path="download-app-google" />
              </a>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
